import { action, computed, makeObservable, observable, toJS } from 'mobx';

import AnalyticsService, { GaPageLabel } from '../services/AnalyticsService';
import ApiService from '../services/ApiService';

import EInvoiceSuppliersDataStore from './data/EInvoiceSuppliersDataStore';
import InvoiceSettingsPanelStore from './InvoiceSettingsPanelStore';
import InvoiceSettingsStore from './InvoiceSettingsStore';
import { IModalStore, ModalType } from './ModalStore';
import { ISsoStore } from './SsoStore';

export default class InvoiceSettingsModalStore {
  @observable
  public isSaveSuccessful = false;
  @observable
  public isSaving = false;

  constructor(
    private apiService: ApiService,
    private ssoStore: ISsoStore,
    public invoiceSettingsPanelStore: InvoiceSettingsPanelStore,
    private invoiceSettingsStore: InvoiceSettingsStore,
    private eInvoiceSuppliersDataStore: EInvoiceSuppliersDataStore,
    private modalStore: IModalStore,
    private analyticsService: AnalyticsService
  ) {
    makeObservable(this);
  }

  @action
  public async openModal(referenceNumber: string): Promise<any> {
    this.setSaving(false);
    this.setSaveSuccessful(false);
    this.invoiceSettingsPanelStore.initData();
    this.modalStore.openModal(ModalType.InvoiceSettingsModal);
    await this.invoiceSettingsStore.tryLoadInvoiceSettings();
    await this.eInvoiceSuppliersDataStore.tryLoadEInvoiceSuppliers();
    this.invoiceSettingsPanelStore.setSettings(toJS(this.invoiceSettingsStore.invoiceSettings(referenceNumber)));
  }

  @action
  public closeModal(): void {
    this.modalStore.closeModal();
  }

  @computed
  public get gaPageLabel(): GaPageLabel {
    return this.modalStore.gaPageLabel;
  }

  @computed
  public get invoiceSettingsLoadedWithError(): boolean {
    return this.invoiceSettingsStore.customerInvoicesSettings.isFinishedWithError;
  }

  @computed
  public get invoiceSource(): string {
    return this.invoiceSettingsPanelStore.invoiceSource;
  }

  public invoiceSettingsChanges(): string {
    return this.analyticsService.getInvoiceSettingsSaveChanges(this.invoiceSettingsPanelStore.originalSettings, this.invoiceSettingsPanelStore.invoiceSettings);
  }

  @action
  public async saveInvoiceSettings(): Promise<'success' | 'validation-error' | 'save-error'> {
    this.setSaving(true);

    try {
      this.invoiceSettingsPanelStore.trimInvoiceSettings();
      await this.invoiceSettingsPanelStore.validateInvoiceSettings();
      if (!this.invoiceSettingsPanelStore.hasValidationError) {
        const settings = this.invoiceSettingsPanelStore.invoiceSettings;

        settings.invoiceCentralAddressType = this.invoiceSettingsPanelStore.invoiceCentralAddressType;

        const saveResult = await this.apiService.saveInvoiceSettings(this.ssoStore.customer.customerId, settings.refNumber, {
          ...settings,
          bankCode: this.invoiceSettingsPanelStore.bankAddressType === 'IBAN' ? null : settings.bankCode,
          bankRecipientCode: settings.isRecipientSameAsCustomer ? null : settings.bankRecipientCode
        });
        if (saveResult.result === 'SOURCE_VALIDATION_FAILED') {
          this.invoiceSettingsPanelStore.markSaveFailed();
          this.invoiceSettingsPanelStore.setRuleViolation();
          return 'save-error';
        }
        this.invoiceSettingsStore.setInvoiceSettings(settings.refNumber, settings);
        this.modalStore.closeModal();
        this.setSaveSuccessful(true);
        return 'success';
      } else {
        return 'validation-error';
      }
    } catch (e) {
      console.error(e);
      this.invoiceSettingsPanelStore.markSaveFailed();
      return 'save-error';
    } finally {
      this.setSaving(false);
    }
  }

  @action
  private setSaveSuccessful(successful: boolean) {
    this.isSaveSuccessful = successful;
  }

  @action
  private setSaving(saving: boolean) {
    this.isSaving = saving;
  }
}
